import { useState } from "react";
import { useShallow } from "zustand/react/shallow";
import type { CatalogItemDto, CatalogShop, HomeStageOfferDto } from "@townsy/contracts";
import { useTownSession } from "../../app/TownSessionContext";
import { stageLabel } from "../../homes/homeLogic";
import { useTownStore } from "../../state/store";
import { ItemPurchaseDialog, UpgradeDialog } from "./HomeReviewDialogs";
import { HomeShopView, SHOP_INFO } from "./HomeShopView";
import { itemCheckout, upgradeCheckout } from "../../commerce/checkoutTargets";
import { useDollarGate } from "../checkout/useDollarGate";
import { useHomeActions, useHomeData } from "./useHomeData";

/**
 * Townsy Home Builders (v-02) / Townsy Furnishings (v-03) inside the 3D town: like the Real Estate
 * office, the panel opens by itself while the visitor is inside the shop and can be dismissed and
 * reopened from the HUD there. The My home manager takes the same side of the screen while open.
 */
export function HomeShopPanel({ shop }: { shop: CatalogShop }) {
  const session = useTownSession();
  const { open, inShop, selected, chatOpen, myHomeOpen } = useTownStore(
    useShallow((s) => ({
      open: shop === "home_builders" ? s.ui.homeBuildersOpen : s.ui.furnishingsOpen,
      inShop: Boolean(s.room.kind === "interior" && s.room.propertyId && session?.civicKind(s.room.propertyId) === shop),
      selected: s.ui.selectedPropertyId,
      chatOpen: s.ui.chatOpen,
      myHomeOpen: s.ui.myHomeOpen,
    })),
  );
  const setUi = useTownStore((s) => s.setUi);
  const pushToast = useTownStore((s) => s.pushToast);
  const visible = open && inShop;
  const data = useHomeData(inShop);
  const actions = useHomeActions(session);
  const dollar = useDollarGate();
  const openCheckout = useTownStore((s) => s.openCheckout);
  const [buying, setBuying] = useState<CatalogItemDto | null>(null);
  const [upgrading, setUpgrading] = useState<HomeStageOfferDto | null>(null);
  // Mobile: one primary bottom sheet at a time (docs/01 section 6) — the property sheet wins.
  const small = typeof window !== "undefined" && window.innerWidth <= 900;
  if (!visible || myHomeOpen || (small && selected)) return null;

  const info = SHOP_INFO[shop];
  const officeId = session?.realEstateOfficeId ?? null;

  return (
    <section
      className={`panel panel-left real-estate-panel shop-panel${chatOpen ? " compact" : ""}`}
      role="region"
      aria-labelledby={`${shop}-title`}
      data-testid={`${shop}-panel`}
    >
      <div className="sheet-handle" aria-hidden="true" />
      <div className="panel-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 id={`${shop}-title`}>{info.name}</h2>
          <div className="small muted">
            {info.address} · {info.tagline}
          </div>
        </div>
        <button type="button" className="btn btn-ghost icon-btn" aria-label={`Close ${info.name}`} onClick={() => setUi(shop === "home_builders" ? { homeBuildersOpen: false } : { furnishingsOpen: false })}>
          ×
        </button>
      </div>
      <div className="panel-body">
        <HomeShopView
          shop={shop}
          data={data}
          onBuy={setBuying}
          onUpgrade={setUpgrading}
          dollar={dollar}
          onBuyUsd={(item) => openCheckout(itemCheckout(item))}
          onUpgradeUsd={(next) => data.home && openCheckout(upgradeCheckout(data.home, next))}
          onSaveLook={async (look) => (data.home ? actions.saveLook(data.home, look) : { ok: false, code: "HOME_REQUIRED", message: "Buy a plot first.", conflict: false })}
          onOpenMyHome={(tab) => session?.openMyHome(tab)}
          realEstateAction={
            officeId ? (
              <button type="button" className="btn btn-sm" onClick={() => void session?.fastTravel(officeId)} data-testid="go-to-real-estate">
                Go to Townsy Real Estate
              </button>
            ) : null
          }
        />
      </div>
      {buying && data.wallets ? (
        <ItemPurchaseDialog
          item={buying}
          coinsAvailable={data.wallets.coins.available}
          onClose={() => setBuying(null)}
          onStale={actions.refreshStale}
          onPurchased={(res) => {
            const item = buying;
            setBuying(null);
            actions.afterItemPurchase(res);
            pushToast(`${item.name} is in your storage. Place it from My home.`, "success", {
              label: "Place it",
              run: () => session?.openMyHome(item.kind === "YARD" ? "yard" : "interior"),
            });
          }}
        />
      ) : null}
      {upgrading && data.home && data.wallets ? (
        <UpgradeDialog
          home={data.home}
          next={upgrading}
          coinsAvailable={data.wallets.coins.available}
          onClose={() => setUpgrading(null)}
          onStale={actions.refreshStale}
          onUpgraded={(res) => {
            setUpgrading(null);
            actions.afterUpgrade(res);
            pushToast(`Your home is now a ${stageLabel(res.stage).toLowerCase()}. Everyone in town sees it.`, "success", {
              label: "See it",
              run: () => session?.goToProperty(res.propertyId),
            });
          }}
        />
      ) : null}
    </section>
  );
}
